
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import IBAN from 'iban';
import {
  AttachmentType, BoxStatusType, Connection, Lead,
} from '@/types';
import UpdateButton from '@/components/UpdateButton.vue';
import SwitchWithConfirm from '@/components/SwitchWithConfirm.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import WeekPickerDialog from '@/components/WeekPickerDialog.vue';
import { getProvince } from '@/lib/italy';

moment.locale('it');

@Component({
  components: {
    UpdateButton,
    SwitchWithConfirm,
    DatePickerDialog,
    WeekPickerDialog,
  },
})
export default class EditConnectionDialogMixin extends Vue {
  @Prop(Boolean) readonly value!: boolean;

  dialog = false
  valid = false

  tab: number | null = null

  datePickerDialog = false
  weekPickerDialog = false
  datePickerDate: string | null = null
  datePickerName: string | null = null

  @Watch('value')
  onValueChange(val: boolean) {
    this.dialog = val;

    // Se stiamo aprendo il dialog passa alla prima scheda
    if (val) {
      // Inizializza tutti i campi del modulo
      this.refresh();

      this.tab = 0;
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }

    // Se stiamo chiudendo il dialog, deseleziona la Connection
    if (!val) {
      this.$store.dispatch('unselectConnection');
    }
  }

  // Ritorna `true` se i campi del modulo devono essere per qualche motivo tutti disattivati
  get fieldsDisabled(): boolean {
    return this.lead?.status !== 'open'
      || this.loading
      || this.updating;
  }

  get appendIcon(): string | null { return this.fieldsDisabled ? 'mdi-lock' : null; }

  // La Connection selezionata
  get connection(): Connection | null {
    return this.$store.getters.currentConnection as Connection | null;
  }

  // La Lead selezionata (associata alla Connection)
  get lead(): Lead | null {
    return this.$store.getters.currentLead as Lead | null;
  }

  // La Lead figlia elettrica della lead selezionata (associata alla Connection)
  get currentElettricaFiglia(): Lead | null {
    return this.$store.getters.currentElettricaFiglia as Lead | null;
  }

  // La Connection è in caricamento?
  get loading(): boolean {
    // La Connection è in caricamento anche se la lead figlia elettrica è in caricamento
    return this.$store.getters.isConnectionLoading || this.$store.getters.currentElettricaFiglia === null;
  }

  // La Connection è in aggiornamento?
  get updating(): boolean {
    return this.$store.getters.isConnectionUpdating;
  }

  // È necessario salvare le modifiche alla Connection?
  get dirty(): boolean {
    return this.$store.getters.isConnectionDirty;
  }

  get hasPracticeNotes(): boolean {
    return this.connection?.hasPracticeNotes === true;
  }

  get ibanRequiredRules() {
    return [
      (v: string) => !!v || "L'IBAN è richiesto",
      (v: string) => IBAN.isValid(v) || "L'IBAN inserito non è corretto",
    ];
  }

  get emailRequiredRules() {
    return [
      (v: string) => !!v || "L'indirizzo email è richiesto",
      (v: string) => /^\s*[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\s*$/
        .test(v) || "L'indirizzo email non è un indirizzo corretto",
    ];
  }

  get emailRules() {
    return [
      (v: string) => !v || /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
        .test(v) || "L'indirizzo email non è un indirizzo corretto",
    ];
  }

  get podRequiredRules() {
    return [
      (v: string) => !!v || 'IL POD è richiesto',
      (v: string) => /^IT\d\d\dE\d\d\d\d\d\d\d\d(\d)?$/i
        .test(v?.replace(/ /g, '').toLowerCase()) || 'Il POD inserito non è corretto',
    ];
  }

  get toponimoOptions() {
    return [
      { text: 'Via', value: 'via' },
      { text: 'Piazza', value: 'piazza' },
      { text: 'Corso', value: 'corso' },
      { text: 'Viale', value: 'viale' },
      { text: 'Largo', value: 'largo' },
      { text: 'Vicolo', value: 'vicolo' },
      { text: 'Strada', value: 'strada' },
    ];
  }

  get provinces() {
    return getProvince();
  }

  mDate(date: Date | null | undefined): string | null { return date ? moment(date).format('L') : null; }

  // Aggiorna i campi con gli eventuali nuovi valori della Connection/Lead selezionata
  mixinRefresh() {
    console.log(`${this.constructor.name}: mixinRefresh()`); // eslint-disable-line no-console
  }
  refresh() { return this.mixinRefresh(); }

  mixinOnFieldUpdated(field: string, value: unknown = undefined) {
    if (value !== undefined) console.log(`${this.constructor.name}: mixinOnFieldUpdated(${field}, ${value})`); // eslint-disable-line no-console
    else console.log(`${this.constructor.name}: mixinOnFieldUpdated(${field})`); // eslint-disable-line no-console

    if (value !== undefined) this[field] = value;

    const update = {} as { [key: string]: unknown };

    update[field] = this[field] ?? null;

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }
  onFieldUpdated(field: string, value: unknown = undefined) {
    return this.mixinOnFieldUpdated(field, value);
  }

  mixinOnSwitchChange(name: string) {
    console.log(`${this.constructor.name}: mixinOnSwitchChange(${name})`); // eslint-disable-line no-console

    // Se lo switch non esiste si ferma subito
    if (name in this === false) { return; }

    // Il valore del modello dello switch è già stato aggiornato.
    // Proseguo preparando l'aggiornamento tramite API.

    const update = {} as { [key: string]: unknown };

    update[name] = this[name];

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }
  onSwitchChange(name: string) { return this.mixinOnSwitchChange(name); }

  mixinUpdateAttachmentStatus(type: AttachmentType, status: BoxStatusType) {
    console.log(`${this.constructor.name}: mixinUpdateAttachmentStatus(${type}, ${status})`); // eslint-disable-line no-console

    // Se lo stato non è uno di quelli gestiti, si ferma subito
    if (type in this === false) { return; }

    (this[type] as BoxStatusType) = status;

    const update = {} as { [key: string]: unknown };

    update[type] = status;

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }
  updateAttachmentStatus(type: AttachmentType, status: BoxStatusType) {
    return this.mixinUpdateAttachmentStatus(type, status);
  }

  onClickDate(name: string) {
    console.log(`${this.constructor.name}: onClickDate(${name})`); // eslint-disable-line no-console

    // Imposta per quale campo verrà utilizzato il DatePicker
    this.datePickerName = name;

    // Imposta l'eventuale data precedentemente selezionata
    this.datePickerDate = this[name]
      ? moment(this[name]).toISOString(true).substring(0, 10)
      : null;

    // Apre il dialog per la selezione della data
    this.datePickerDialog = true;
  }

  onSelectDate(date: string | null) {
    console.log(`${this.constructor.name}: onSelectDate() date =`, date); // eslint-disable-line no-console

    if (!this.datePickerName) return;

    // Chiudo il dialog per la selezione della data
    this.datePickerDialog = false;

    const update = {} as { [key: string]: unknown };

    // Trasforma in un valore di tipo Date prima di restituirlo
    const selectedDate = date
      ? moment(date, 'YYYY-MM-DD').toDate()
      : null;

    // Inserisco la data nel modulo e nell'aggiornamento
    if (this.datePickerName in this) {
      this[this.datePickerName] = selectedDate;
      update[this.datePickerName] = this[this.datePickerName];
    }

    // Modifico eventuali campi collegati
    switch (this.datePickerName) {
      default:
        break;
    }

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }

  onClearDate(name: string) {
    console.log(`${this.constructor.name}: onClearDate(${name})`); // eslint-disable-line no-console

    const update = {} as { [key: string]: unknown };

    // Cancello la data nel modulo e nel prossimo aggiornamento
    if (name in this) {
      this[name] = null;
      update[name] = null;
    }

    // Modifico eventuali campi collegati
    switch (name) {
      default:
        break;
    }

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }

  onCancel() {
    console.log(`${this.constructor.name}[mixin]: onCancel()`); // eslint-disable-line no-console

    this.dialog = false;

    this.$emit('cancel');
  }

  async onSave() {
    console.log(`${this.constructor.name}[mixin]: onSave()`); // eslint-disable-line no-console

    await this.$store.dispatch('updateSelectedConnection');

    this.dialog = false;
  }
}
